import React from "react";
import { Box, Button, Typography } from "@mui/material";
import InboxIcon from "@mui/icons-material/Inbox";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";

export interface EmptyStateProps {
  isSearchActive: boolean;
  onClearSearch: () => void;
  canAdd: boolean;
  onAdd: () => void;
  entityLabel?: string;
}

export function EmptyState(props: EmptyStateProps) {
  const { isSearchActive, onClearSearch, canAdd, onAdd, entityLabel = "トスアップ" } = props;
  const isLoading = useAtomValue(AppAtoms.isLoading);
  const [initialized, setInitialized] = React.useState(false);
  React.useEffect(() => { setInitialized(true); }, []);  // cmd-po_1151 rw1: mount 直後に立てる（isLoading 依存を外す）

  // cmd-po_1151: ローディング表示は全体 Backdrop（App.tsx）に一本化。
  // initialized ガード（!isLoading && initialized で初めて 0 件メッセージを描画）は維持。
  if (isLoading || !initialized) return null;

  return (
    <Box
      role="status"
      data-testid="empty-state"
      className="po-card empty-state"
      sx={{
        p: "var(--sp-6, 32px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "var(--sp-3, 12px)",
        mt: 3,
      }}
    >
      <Box
        data-testid="empty-state-icon"
        className="es-icon"
        sx={{
          width: 44,
          height: 44,
          color: "var(--color-sumi-muted, #9e9e9e)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isSearchActive ? (
          <SearchOffIcon sx={{ fontSize: 44 }} />
        ) : (
          <InboxIcon sx={{ fontSize: 44 }} />
        )}
      </Box>
      <Typography
        data-testid="empty-state-title"
        component="h2"
        sx={{
          fontSize: "15px",
          fontWeight: 700,
          color: "var(--color-sumi, #1a1a1a)",
          m: 0,
          textAlign: "center",
        }}
      >
        {isSearchActive
          ? `条件に一致する${entityLabel}がありません`
          : `${entityLabel}はまだ登録されていません`}
      </Typography>
      <Typography
        data-testid="empty-state-desc"
        component="p"
        sx={{
          fontSize: "12px",
          color: "var(--color-sumi-light, #666)",
          maxWidth: "420px",
          lineHeight: 1.8,
          m: 0,
          textAlign: "center",
        }}
      >
        {isSearchActive
          ? "検索条件を変更するか、クリアして再度お試しください。"
          : canAdd
            ? `${entityLabel}を登録すると、ここに一覧が表示されます。`
            : `${entityLabel}が登録されると、ここに一覧が表示されます。`}
      </Typography>
      {isSearchActive && (
        <Button
          data-testid="empty-state-clear-btn"
          variant="outlined"
          size="small"
          onClick={onClearSearch}
          sx={{ fontSize: "13px" }}
        >
          検索条件をクリア
        </Button>
      )}
      {!isSearchActive && canAdd && (
        <Button
          data-testid="empty-state-add-btn"
          variant="contained"
          size="small"
          onClick={onAdd}
          sx={{ fontSize: "13px" }}
        >
          {entityLabel}を登録
        </Button>
      )}
    </Box>
  );
}
